import { DEFAULT_LOCALE, dictionaries, type Locale } from './i18n/index.js';
import type { LandingCopy, LandingSection } from './types.js';

/**
 * MKT-001 — which words a landing section shows.
 *
 * Two sources, and the order between them is the whole rule: what an editor
 * typed into the page wins, and everything else comes from the locale files
 * like every other string in the product. That is what lets the page be
 * editable without leaving it monolingual — an untouched section is still a
 * translated one, and clearing an edit is the same act as restoring it.
 *
 * It lives in shared because both readers need the same answer: the web app
 * renders from it, and anything server-side that ever quotes the page (a
 * preview, a mail) must not resolve the text differently.
 */

const EMPTY: LandingCopy = { eyebrow: null, heading: null, body: null, bullets: [], ctaLabel: null };

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Reads `landing.sections.hero` and the like out of a locale file. */
function fromDictionary(contentKey: string, locale: Locale): LandingCopy | null {
  const node = contentKey.split('.').reduce<unknown>(
    (value, part) =>
      value && typeof value === 'object' ? (value as Record<string, unknown>)[part] : undefined,
    dictionaries[locale],
  );
  if (!node || typeof node !== 'object') return null;

  const block = node as Record<string, unknown>;
  return {
    eyebrow: text(block.eyebrow),
    heading: text(block.heading),
    body: text(block.body),
    bullets: Array.isArray(block.bullets)
      ? block.bullets.filter((line): line is string => typeof line === 'string')
      : [],
    ctaLabel: text(block.ctaLabel),
  };
}

export function landingCopy(section: LandingSection, locale: Locale): LandingCopy {
  const edited = section.overrides[locale];
  if (edited) return edited;

  if (section.contentKey) {
    return (
      fromDictionary(section.contentKey, locale) ??
      fromDictionary(section.contentKey, DEFAULT_LOCALE) ??
      EMPTY
    );
  }

  /*
   * A section somebody added in the page itself has no shipped text to fall
   * back on. Showing it in the language it was written in is not a translation,
   * but it beats a blank block on a marketing page.
   */
  return section.overrides[DEFAULT_LOCALE] ?? Object.values(section.overrides)[0] ?? EMPTY;
}

/** True when this section carries an edit for `locale` rather than shipped text. */
export function isEdited(section: LandingSection, locale: Locale): boolean {
  return Boolean(section.overrides[locale]);
}
